<template>
  <div>
    <oie-base-action
      v-tooltip.top="{ content: tStatic('uploadFile') }"
      theme="outlined"
      @click.stop="open">
      <template #iconPrepend>
        <oie-base-icon icon="cloud-upload" size="s"></oie-base-icon>
      </template>
      {{ tStatic('uploadFile') }}
    </oie-base-action>
    <oie-portal-wrapper :name="name">
      <template #title>
        <h2>{{ tStatic('uploadFileTitle') }}</h2>
      </template>
      <div v-element-loader="{endpoint: 'shoppinglist/uploadFile'}" class="file-upload-slide-in__content">
        <slot></slot>
        <label
          tabindex="0"
          :for="`${name}-file-input`"
          class="file-upload-slide-in__input">
          <span>{{ tStatic('fileUpload') }}</span>
          <oie-base-icon icon="cloud-upload" size="s"></oie-base-icon>

          <input
            :id="`${name}-file-input`"
            ref="fileInput"
            type="file"
            v-bind="$attrs"
            hidden
            @change="handleFileChange">
        </label>
        <!-- UPLOAD FEEDBACK -->
        <div>
          <oie-message-bar
            v-if="successItemsCountText"
            :text="successItemsCountText"
            is-type="success"
            :close-button-text="tStatic('genericClose')">
            <template #close>
              <button
                class="message-bar__dismiss-actions"
                :aria-label="tStatic('dismissMessage')"
                @click="uploadFileResponse.successItemsCount = ''">
                <oie-use-svg svg="close" class="message-bar__x-icon"></oie-use-svg>
              </button>
            </template>
          </oie-message-bar>

          <oie-message-bar
            v-if="formatErrorText"
            :text="formatErrorText"
            is-type="error">
            <template #close>
              <button
                class="message-bar__dismiss-actions"
                :aria-label="tStatic('dismissMessage')"
                @click="uploadFileResponse.formattingListErrors = []">
                <oie-use-svg svg="close" class="message-bar__x-icon"></oie-use-svg>
              </button>
            </template>
          </oie-message-bar>

          <oie-message-bar
            v-if="otherErrorText"
            :text="otherErrorText"
            is-type="error"
            :close-button-text="tStatic('genericClose')">
            <template #close>
              <button
                class="message-bar__dismiss-actions"
                :aria-label="tStatic('dismissMessage')"
                @click="uploadFileResponse.otherErrorsList = []">
                <oie-use-svg svg="close" class="message-bar__x-icon"></oie-use-svg>
              </button>
            </template>
          </oie-message-bar>
        </div>
        <template v-if="uploadFileResponse && uploadFileResponse.mandatoryComment">
          <p>{{ tStatic('mandatoryCommentsRequired') }}</p>
          <oie-mandatory-comments v-model="uploadFileResponse.mandatoryComment"></oie-mandatory-comments>
        </template>
        <oie-action-wrapper v-if="uploadFileResponse">
          <template #left>
            <oie-base-action :disabled="!canAddRows" size="large" @click="addRowsToShoppingList">
              {{ tStatic('addFileToShoppingList') }}
            </oie-base-action>
            <oie-base-action theme="skeleton" size="large" @click="close">
              {{ tStatic('genericClose') }}
            </oie-base-action>
          </template>
        </oie-action-wrapper>
      </div>
    </oie-portal-wrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import httpService from '@/core/http-service'

export default {
  data: () => {
    return {
      uploadFileResponse: null
    }
  },
  computed: {
    name() {
      return _.uniqueId('file-upload-slide-in-')
    },
    successItemsCountText() {
      return (this.uploadFileResponse && this.uploadFileResponse.successItemsCount) ? this.tStatic('quickOrderFileSuccessAmountText').replace('{0}', this.uploadFileResponse.successItemsCount) : ''
    },
    formatErrorText() {
      return (this.uploadFileResponse && this.uploadFileResponse.formattingListErrors.length) ? this.tStatic('quickOrderFileFormatErrorText').replace('{0}', _.map(this.uploadFileResponse.formattingListErrors, (id) => { return id }).join(', ')) : ''
    },
    otherErrorText() {
      return (this.uploadFileResponse && this.uploadFileResponse.otherErrorsList.length) ? this.tStatic('quickOrderFileOtherErrorText').replace('{0}', _.map(this.uploadFileResponse.otherErrorsList, (id) => { return id }).join(', ')) : ''
    },
    canAddRows() {
      const hasSuccessfulItems = !!this.uploadFileResponse.successItemsCount
      const mandatoryCommentsSatisfied = !this.uploadFileResponse.mandatoryComment || _.chain(this.uploadFileResponse.mandatoryComment).flatMap(val => val).every(comment => comment.value).value()
      return hasSuccessfulItems && mandatoryCommentsSatisfied
    }
  },
  methods: {
    ...mapActions('ui', ['setActiveSlideIn']),
    open() {
      this.setActiveSlideIn(this.name)
    },
    close() {
      this.uploadFileResponse = null
      this.setActiveSlideIn(null)
    },
    async handleFileChange(event) {
      const file = _.get(event, 'target.files[0]')
      if(file) {
        this.uploadFileResponse = (await httpService.uploadFile('shoppinglist/uploadFile', file)).data
      }
    },
    async addRowsToShoppingList() {
      _.forEach(this.uploadFileResponse.data.rows, row => {
        if(this.uploadFileResponse.mandatoryComment && this.uploadFileResponse.mandatoryComment[row.itemId]) {
          row.mandatoryComment = this.uploadFileResponse.mandatoryComment[row.itemId]
        }
      })

      this.$emit('add-file-to-list', this.uploadFileResponse.data.rows)
      this.close()
    }
  }
}
</script>

<style lang="scss">
.file-upload-slide-in__content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: measure(half);
}

.file-upload-slide-in__input {
  display: inline-flex;
  align-items: center;
  gap: measure(quarter);
  padding: 6px 16px;
  height: 32px;
  border-radius: 2px;
  cursor: pointer;
  color: color(brand-blue-3);
  border: 1px solid color(brand-blue-3);
  background-color: transparent;
}

.file-upload-slide-in__input:hover {
  background-color: color(blue-4);
}

.file-upload-slide-in__input:disabled {
  color: color(gray-2);
  border: 1px solid color(gray-4);
}

</style>
